import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage'; 
import { getCurrentUser } from 'aws-amplify/auth'; // Añadido para identificar al usuario
import { getBookOrder, listUserNotes } from '../graphql/queries';
import { createUserNote, deleteUserNote } from '../graphql/mutations';
import { View, Heading, Flex, Image, Button, Loader, Text, TextAreaField, Card } from '@aws-amplify/ui-react';
import { FiArrowLeft, FiShield, FiEdit3, FiSave, FiTrash2, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import logo from '../logo.png';

const ReaderPage = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const client = useMemo(() => generateClient(), []);
    
    const [bookDetails, setBookDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [safeImageUrl, setSafeImageUrl] = useState(null);
    const [showNotes, setShowNotes] = useState(false);
    const [currentNote, setCurrentNote] = useState(""); 
    const [notesHistory, setNotesHistory] = useState([]); 
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentUser, setCurrentUser] = useState(null); // Estado para el usuario actual

    const loadNotes = useCallback(async (targetBookID, userId) => {
        if (!targetBookID || !userId) return;
        try {
            const notesRes = await client.graphql({
                query: listUserNotes,
                // CORRECCIÓN: Filtramos por libro Y por dueño (owner/userId)
                variables: { 
                    filter: { 
                        bookID: { eq: targetBookID },
                        owner: { eq: userId } 
                    } 
                },
                authMode: 'userPool'
            });
            const items = notesRes.data.listUserNotes.items || [];
            const sorted = [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setNotesHistory(sorted);
        } catch (err) { console.error("❌ Error notas:", err); }
    }, [client]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);

        const loadInitialData = async () => {
            if (!orderId) return;
            try {
                // Obtener usuario actual para filtrar sus notas
                const user = await getCurrentUser();
                setCurrentUser(user);

                const orderRes = await client.graphql({
                    query: getBookOrder,
                    variables: { id: orderId },
                    authMode: 'userPool'
                });
                
                const order = orderRes.data.getBookOrder;
                if (!order || order.status !== 'APROBADO') {
                    navigate('/profile');
                    return;
                }

                const bookRes = await client.graphql({
                    query: /* GraphQL */ `
                        query GetBookReader($id: ID!) {
                            getBook(id: $id) {
                                id
                                title
                                totalPages
                                s3FolderName
                                imagePrefix
                                onlineExtension
                            }
                        }
                    `,
                    variables: { id: order.bookID },
                    authMode: 'apiKey' 
                });
                
                const bookData = bookRes.data.getBook;
                if (bookData) {
                    setBookDetails(bookData);
                    loadNotes(bookData.id, user.username || user.userId);
                }
            } catch (error) { console.error("❌ ERROR:", error); }
            finally { setLoading(false); }
        };
        loadInitialData();
        return () => window.removeEventListener('resize', handleResize);
    }, [orderId, navigate, client, loadNotes]);

    useEffect(() => {
        const fetchImage = async () => {
            if (!bookDetails) return;
            try {
                const folder = bookDetails.s3FolderName || bookDetails.id;
                const ext = bookDetails.onlineExtension || 'png';
                const pageNum = String(currentPage).padStart(3, '0');
                const prefix = bookDetails.imagePrefix || 'P-';
                const fullPath = `public/books-data/${folder}/${prefix}${pageNum}.${ext}`;
                
                const signedUrl = await getUrl({ 
                    path: fullPath,
                    options: { expiresIn: 3600 }
                });
                setSafeImageUrl(signedUrl.url.toString());
            } catch (err) { setSafeImageUrl(null); }
        };
        fetchImage();
    }, [currentPage, bookDetails]);

    const handleSaveNote = async () => {
        if (!currentNote.trim() || !bookDetails || !currentUser) return;
        try {
            await client.graphql({
                query: createUserNote,
                variables: { 
                    input: { 
                        bookID: bookDetails.id, 
                        content: currentNote, 
                        pageNumber: currentPage,
                        owner: currentUser.username || currentUser.userId // Aseguramos el dueño
                    } 
                },
                authMode: 'userPool'
            });
            setCurrentNote("");
            loadNotes(bookDetails.id, currentUser.username || currentUser.userId); 
        } catch (err) { alert("Error al guardar."); }
    };

    const handleDeleteNote = async (id) => {
        try {
            await client.graphql({
                query: deleteUserNote,
                variables: { input: { id } },
                authMode: 'userPool'
            });
            setNotesHistory(prev => prev.filter(n => n.id !== id));
        } catch (err) { console.error(err); }
    };

    if (loading) return <Flex justifyContent="center" padding="100px"><Loader size="large" /><Text>Protegiendo Contenido...</Text></Flex>;

    return (
        <View height="100vh" display="grid" style={{ gridTemplateRows: 'auto 1fr auto', backgroundColor: '#1a1a1b', overflow: 'hidden' }}>
            
            <Flex padding={isMobile ? "10px" : "10px 30px"} backgroundColor="white" alignItems="center" justifyContent="space-between" boxShadow="0 2px 10px rgba(0,0,0,0.3)" zIndex="10">
                <Button onClick={() => navigate('/profile')} variation="link" color="#001f3f">
                    <FiArrowLeft size="20px" /> {!isMobile && "Biblioteca"}
                </Button>
                
                <Flex direction="column" alignItems="center">
                    <Image src={logo} alt="Logo" height={isMobile ? "25px" : "35px"} />
                    <Text fontSize="12px" color="#D4AF37" fontWeight="bold" textAlign="center">{bookDetails?.title}</Text>
                </Flex>

                <Button onClick={() => setShowNotes(!showNotes)} style={{ backgroundColor: showNotes ? '#001f3f' : '#D4AF37', color: 'white' }}>
                    <FiEdit3 /> {!isMobile && (showNotes ? "Cerrar" : "Notas")}
                </Button>
            </Flex>

            <Flex direction="row" flex="1" overflow="hidden" position="relative">
                <View flex="1" padding={isMobile ? "10px" : "20px"} style={{ overflowY: 'auto', textAlign: 'center' }}>
                    <div onContextMenu={(e) => e.preventDefault()} style={{ userSelect: 'none', position: 'relative', display: 'inline-block' }}>
                        <Card backgroundColor="white" padding="5px" boxShadow="0 0 30px rgba(0,0,0,0.7)">
                            {safeImageUrl ? (
                                <Image src={safeImageUrl} alt="Cargando..." crossOrigin="anonymous" onDragStart={(e) => e.preventDefault()}
                                    style={{ maxHeight: '75vh', display: 'block', pointerEvents: 'none', WebkitTouchCallout: 'none' }}
                                />
                            ) : <Loader />}
                        </Card>
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'transparent', zIndex: 5 }}></div>
                    </div>

                    <Flex gap="20px" justifyContent="center" marginTop="20px" alignItems="center">
                        <Button 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                            disabled={currentPage === 1} 
                            backgroundColor="rgba(255,255,255,0.1)"
                            color="white"
                            style={{ borderRadius: '8px', border: '1px solid #D4AF37', minWidth: '120px' }}
                        >
                            <FiChevronLeft /> Anterior
                        </Button>
                        
                        <Text color="#D4AF37" fontWeight="bold" fontSize="13px" style={{ letterSpacing: '1px' }}>
                            NAVEGACIÓN DE LECTURA
                        </Text>

                        <Button 
                            onClick={() => setCurrentPage(p => Math.min(bookDetails?.totalPages || 999, p + 1))} 
                            disabled={currentPage >= (bookDetails?.totalPages || 999)} 
                            backgroundColor="rgba(255,255,255,0.1)"
                            color="white"
                            style={{ borderRadius: '8px', border: '1px solid #D4AF37', minWidth: '120px' }}
                        >
                            Siguiente <FiChevronRight />
                        </Button>
                    </Flex>
                </View>

                {showNotes && (
                    <Card width={isMobile ? "100%" : "350px"} backgroundColor="#f4f4f4" style={{ position: isMobile ? 'absolute' : 'relative', right: 0, top: 0, bottom: 0, borderLeft: '4px solid #D4AF37', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
                        <Flex justifyContent="space-between" alignItems="center" padding="15px" backgroundColor="#001f3f">
                            <Heading level={5} color="white">Mis Notas Privadas</Heading>
                            <Button onClick={() => setShowNotes(false)} variation="link" color="white"><FiX /></Button>
                        </Flex>
                        
                        <View padding="15px" backgroundColor="white">
                            <TextAreaField labelHidden value={currentNote} onChange={e => setCurrentNote(e.target.value)} placeholder="Escribir nota..." rows={2}/>
                            <Button marginTop="10px" onClick={handleSaveNote} width="100%" variation="primary" backgroundColor="#D4AF37" color="black" fontWeight="bold"><FiSave /> Guardar</Button>
                        </View>
                        
                        <View flex="1" style={{ overflowY: 'auto' }} padding="10px">
                            {notesHistory.map(note => (
                                <Card key={note.id} marginTop="10px" padding="12px" borderRadius="8px" backgroundColor="white">
                                    <Flex justifyContent="space-between">
                                        <Text fontSize="10px" fontWeight="bold" color="#D4AF37">PÁG {note.pageNumber} • {new Date(note.createdAt).toLocaleDateString()}</Text>
                                        <FiTrash2 color="red" cursor="pointer" onClick={() => handleDeleteNote(note.id)} />
                                    </Flex>
                                    <Text fontSize="13px" marginTop="5px">{note.content}</Text>
                                </Card>
                            ))}
                        </View>
                    </Card>
                )}
            </Flex>

            <Flex padding="5px" backgroundColor="#001f3f" justifyContent="center">
                <Text color="white" fontSize="10px"><FiShield color="#D4AF37" /> LECTURA PRIVADA PROTEGIDA - {new Date().getFullYear()}</Text>
            </Flex>
        </View>
    );
};

export default ReaderPage;